import React, { useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBan,
  faCheck,
  faDiagramProject,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgDialog from 'modules/Common/ui/CgDialog';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import { cgToast } from 'modules/Common/ui/toast';
import { useSupplierDraft, useSupplierDraftActions } from 'modules/FreeSupplier/useSupplierDrafts';
import { useSupplierFactors } from 'modules/FreeSupplier/useSupplierFactors';
import { SupplierFactorDraftReviewState } from 'modules/FreeSupplier/supplierDraftContracts';
import {
  draftReviewStateLabel,
  draftReviewStateTone,
  formatDraftInterval,
  isPendingDraftReviewState,
} from 'modules/FreeSupplier/supplierDraftPresentation';
import { SupplierRoutePath, buildSupplierDiagramPath } from 'modules/FreeSupplier/routes';
import { useMockData } from '../state/MockDataContext';
import { installationNameById } from '../state/adapters';
import useCnGoodLabel from '../state/useCnGoodLabel';
import ProposedFactorForm from '../components/ProposedFactorForm';
import PublicationBlockersPanel from '../components/PublicationBlockersPanel';
import { parsePublicationBlockers } from 'modules/FreeSupplier/supplierPublicationBlockers';
import { SupplierPanelLoader } from '../components/SupplierLoadingStates';

// Item 9: the backend's overlap rejection (supplierFactorPublication.ts
// publishSupplierFactor → normalizeSeriesKey/createRevision's overlap check)
// is a plain ValidationError, which errorHandler.ts maps to a 400 — NOT the
// 422 this banner used to gate on (so it could never actually show). Some
// OTHER confirm failure could still legitimately be a 422 (e.g. a future
// contract-shaped rejection), so both statuses are accepted, discriminated by
// the response message actually mentioning "overlap" (the exact throw is
// "Supplier factor interval positively overlaps a current interval") — an
// unrelated 400/422 keeps falling through to the generic notifyError toast.
// TODO(S10 fast-follow): a stable machine-readable error code would replace
// this message sniff.
const OVERLAP_ERROR_MESSAGE_PATTERN = /overlap/i;

const isOverlapConfirmError = (error) => {
  if (!error || (error.httpStatus !== 400 && error.httpStatus !== 422)) return false;
  const message = error.payload?.message || error.message || '';
  return OVERLAP_ERROR_MESSAGE_PATTERN.test(message);
};

// New-build surface (D48): the draft review screen — header (series identity +
// state chip + link to the immutable source diagram), the editable
// proposed-factor form (which surfaces the proposed-vs-currently-published
// comparison inline, per field — see ProposedFactorForm), and a footer of
// Discard / Confirm & publish. Every mutation is awaited; nothing
// navigates/closes before success, and a failure preserves entered values
// while disabling only the in-flight action (phase-4 mutation UX contract).
export default function DraftReviewPage() {
  const { t } = useTranslation('freeSupplier');
  const { draftId } = useParams();
  const history = useHistory();
  const { installationDocs } = useMockData();
  const draftQuery = useSupplierDraft(draftId);
  const factorsQuery = useSupplierFactors();
  const { patch, discard, confirm } = useSupplierDraftActions();
  const [discardOpen, setDiscardOpen] = useState(false);
  const cnGoodLabel = useCnGoodLabel();

  const draft = draftQuery.data?.draft;

  const installationName = (id) => installationNameById(installationDocs, id);

  // Item 16A/B: the header renders the CN good as the interactive hover
  // component (below); the discard-confirmation dialog body stays a plain,
  // informational string — no reason to offer an interactive hover trigger
  // inside a "you're about to discard this" confirmation — so it uses the
  // real label helper instead of the removed demo-era cnCodeLabel.
  const seriesLabel = (d) =>
    `${cnGoodLabel(d.targetSeriesKey.cnCode)}${
      d.targetSeriesKey.productVariant ? ` · ${d.targetSeriesKey.productVariant}` : ''
    }`;

  const handleSave = (body) => patch.mutateAsync({ draftId, body });

  const handleDiscard = async () => {
    try {
      await discard.mutateAsync(draftId);
      setDiscardOpen(false);
    } catch {
      // notifyError already surfaced the failure — keep the overlay open so
      // the supplier can retry or cancel.
    }
  };

  const handleConfirm = async () => {
    try {
      await confirm.mutateAsync(draftId);
      // Idempotent re-confirm of an already-published draft also resolves
      // here (same revision) — treated as success either way.
      cgToast.success(t('freeSupplier:draftReview.publishedToastTitle', 'Published'), {
        description: t(
          'freeSupplier:draftReview.publishedToastBody',
          'Your emissions data is now published and shared',
        ),
      });
      history.push(SupplierRoutePath.EmissionFactors);
    } catch {
      // notifyError already toasted; the inline 422-overlap banner below reads
      // confirm.error directly so the guidance persists next to the button.
    }
  };

  if (draftQuery.isPending) {
    return (
      <SupplierPanelLoader
        ariaLabel={t('freeSupplier:draftReview.loading.ariaLabel', 'Loading this draft')}
        messages={[
          t('freeSupplier:draftReview.loading.message1', 'Opening your draft'),
          t('freeSupplier:draftReview.loading.message2', 'Loading the proposed emissions values'),
          t('freeSupplier:draftReview.loading.message3', 'Checking it against your published data'),
        ]}
      />
    );
  }

  if (draftQuery.isError || !draft) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="text-slate-300 text-2xl mb-3"
        />
        <p className="text-sm text-slate-600 m-0">
          {t('freeSupplier:draftReview.notFound.title', "We couldn't find that draft.")}
        </p>
        <p className="text-xs text-slate-400 mt-1 m-0">
          {t(
            'freeSupplier:draftReview.notFound.hint',
            'It may have been discarded, or the link is out of date.',
          )}
        </p>
        <Link
          to={SupplierRoutePath.Drafts}
          className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-primaryColor no-underline"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('freeSupplier:draftReview.notFound.backToDrafts', 'Back to drafts')}
        </Link>
      </div>
    );
  }

  const editable = isPendingDraftReviewState(draft.reviewState);
  const isPublished = draft.reviewState === SupplierFactorDraftReviewState.Published;
  // null unless the last confirm failed the profile-completeness gate.
  const publicationBlockers = parsePublicationBlockers(confirm.error, t);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link
          to={SupplierRoutePath.Drafts}
          className="text-xs text-slate-400 no-underline hover:text-slate-600 inline-flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('freeSupplier:draftReview.backToReviewDrafts', 'Review drafts')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-slate-800 m-0 flex flex-wrap items-center gap-2">
              {installationName(draft.targetSeriesKey.installationId)}
              <span
                className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${draftReviewStateTone(
                  draft.reviewState,
                )}`}
              >
                {draftReviewStateLabel(draft.reviewState)}
              </span>
            </h1>
            <p className="text-sm text-slate-500 mt-1 m-0 flex flex-wrap items-center gap-x-1">
              <CgCnGoodHoverText
                cnCode={draft.targetSeriesKey.cnCode}
                useAutonomousData
                variant="plain"
              />
              <span>
                {draft.targetSeriesKey.productVariant
                  ? `· ${draft.targetSeriesKey.productVariant} · `
                  : '· '}
                {formatDraftInterval(draft.interval)}
              </span>
            </p>
          </div>
          <Link
            to={{
              pathname: buildSupplierDiagramPath(draft.lineage.sourceDiagramId),
              state: { from: 'draft', draftId },
            }}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-xl px-3 py-1.5 no-underline hover:bg-slate-50 transition-colors"
          >
            <FontAwesomeIcon icon={faDiagramProject} />
            {t('freeSupplier:draftReview.viewSourceDiagram', 'View source diagram')}
          </Link>
        </div>
      </div>

      {/* Item B1 (product feedback): the header above stays flush-left/full-width
          like every other page — only the review body below picks a comfortable,
          centered reading width (it's a single-column detail/form surface).
          4xl (not 3xl): the three-across emissions row + the electricity
          breakdown fields overflowed their columns at 3xl. */}
      <div className="flex flex-col gap-6 max-w-4xl w-full mx-auto">
        {!editable ? (
          <div
            className={`rounded-2xl border px-5 py-4 ${
              isPublished ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50'
            }`}
          >
            <p className="text-sm font-semibold text-slate-800 m-0">
              {isPublished
                ? t(
                    'freeSupplier:draftReview.readOnly.publishedTitle',
                    'This draft has been published.',
                  )
                : t(
                    'freeSupplier:draftReview.readOnly.discardedTitle',
                    'This draft has been discarded.',
                  )}
            </p>
            <p className="text-xs text-slate-500 mt-1 m-0">
              {isPublished
                ? t(
                    'freeSupplier:draftReview.readOnly.publishedHint',
                    'It is read-only now. View the published factor and its sync status on the Emissions Data page.',
                  )
                : t(
                    'freeSupplier:draftReview.readOnly.discardedHint',
                    'It is read-only and can no longer be published.',
                  )}
            </p>
            {isPublished ? (
              <Link
                to={SupplierRoutePath.EmissionFactors}
                className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-primaryColor no-underline"
              >
                {t(
                  'freeSupplier:draftReview.readOnly.viewInEmissionsData',
                  'View in Emissions Data →',
                )}
              </Link>
            ) : null}
          </div>
        ) : null}

        {editable ? (
          <>
            {patch.isError && patch.error?.httpStatus === 409 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
                {t(
                  'freeSupplier:draftReview.conflictBanner',
                  'This draft changed — reload and re-apply your edits.',
                )}
              </div>
            ) : null}
            <div className="rounded-2xl bg-white border border-slate-200 p-5">
              <ProposedFactorForm
                key={`${draft.id}-${draft.updatedAt}`}
                draft={draft}
                saving={patch.isPending}
                onSave={handleSave}
                factorItems={factorsQuery.data?.items}
              />
            </div>
          </>
        ) : null}

        {editable ? (
          <div className="flex flex-col gap-2">
            {/* Publishing runs a profile-completeness gate. Its failure carries
                a per-requirement breakdown, so it renders in full here beside
                the button that triggered it — the toast is suppressed for this
                case (see useSupplierDrafts' confirm.onError). */}
            {publicationBlockers ? (
              <PublicationBlockersPanel
                summary={publicationBlockers.summary}
                blockers={publicationBlockers.blockers}
              />
            ) : null}
            {isOverlapConfirmError(confirm.error) ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
                {t(
                  'freeSupplier:draftReview.overlapError',
                  'This period overlaps an existing published factor — adjust the period or correct the existing factor instead.',
                )}
              </div>
            ) : null}
            <div className="flex items-center gap-3">
              <CgRadixButton
                variant="secondary"
                icon={faBan}
                disabled={confirm.isPending || patch.isPending}
                onClick={() => setDiscardOpen(true)}
              >
                {t('freeSupplier:draftReview.discard', 'Discard')}
              </CgRadixButton>
              <CgRadixButton
                variant="primary"
                icon={faCheck}
                loading={confirm.isPending}
                // A2: a save (PATCH) still in flight must block Confirm — otherwise
                // confirm can publish the server's pre-save state while the just-typed
                // values are still on the wire.
                disabled={discard.isPending || patch.isPending}
                onClick={handleConfirm}
              >
                {t('freeSupplier:draftReview.confirmAndPublish', 'Confirm & publish')}
              </CgRadixButton>
            </div>
          </div>
        ) : null}
      </div>

      <CgDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t('freeSupplier:draftReview.discardDialog.title', 'Discard this draft')}
        description={t(
          'freeSupplier:draftReview.discardDialog.description',
          "It will no longer be reviewable or publishable. This can't be undone.",
        )}
        footer={
          <div className="flex items-center justify-end gap-2">
            <CgRadixButton
              variant="ghost"
              onClick={() => setDiscardOpen(false)}
              disabled={discard.isPending}
            >
              {t('freeSupplier:draftReview.discardDialog.cancel', 'Cancel')}
            </CgRadixButton>
            <CgRadixButton
              variant="danger"
              loading={discard.isPending}
              onClick={handleDiscard}
            >
              {t('freeSupplier:draftReview.discardDialog.confirm', 'Discard')}
            </CgRadixButton>
          </div>
        }
      >
        <p className="text-sm text-slate-600 m-0">
          {seriesLabel(draft)} — {formatDraftInterval(draft.interval)}
        </p>
      </CgDialog>
    </div>
  );
}
